/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { BulkSelectionsMetadata } from "store/duck/menu.type"
import { Department } from "types/models/menu"

type ItemParentIDs = { subDeptID: string; deptID: string }

/**
 * Count items for a specific subdepartment from a map of itemID -> parentIDs
 */
export function countItemsForSubDept(
  items: { [itemID: string]: ItemParentIDs } | undefined,
  subDeptID: string,
): number {
  if (!items) return 0
  return Object.values(items).filter(p => p.subDeptID === subDeptID).length
}

/**
 * Count items for a specific department from a map of itemID -> parentIDs
 */
export function countItemsForDept(
  items: { [itemID: string]: ItemParentIDs } | undefined,
  deptID: string,
): number {
  if (!items) return 0
  return Object.values(items).filter(p => p.deptID === deptID).length
}

/**
 * Group items by subdept ID and return counts
 * Can accept either object or array of entries
 */
export function groupItemsBySubDept(
  items:
    | { [itemID: string]: ItemParentIDs }
    | Array<[string, ItemParentIDs]>
    | undefined,
  filterDeptID?: string,
): { [subDeptID: string]: number } {
  const result: { [subDeptID: string]: number } = {}

  if (!items) return result

  const entries = Array.isArray(items) ? items : Object.entries(items)

  entries.forEach(([, parentIDs]) => {
    if (filterDeptID && parentIDs.deptID !== filterDeptID) return
    result[parentIDs.subDeptID] = (result[parentIDs.subDeptID] || 0) + 1
  })

  return result
}

/**
 * Get entries for a specific department from selectedSubDepartments or excludedSubDepartments
 * Returns array of [subDeptID, parentDeptID] entries
 */
export function getSubDeptsForDept(
  subDepts: { [subDeptID: string]: string } | undefined,
  deptID: string,
): Array<[string, string]> {
  if (!subDepts) return []
  return Object.entries(subDepts).filter(
    ([, parentDeptID]) => parentDeptID === deptID,
  )
}

/**
 * Get items for a specific department from selectedItems or excludedItems
 * Returns array of parentIDs objects
 */
export function getItemsForDept(
  items: { [itemID: string]: ItemParentIDs } | undefined,
  deptID: string,
): ItemParentIDs[] {
  if (!items) return []
  return Object.values(items).filter(p => p.deptID === deptID)
}

/**
 * Get item entries (with itemID) for a specific department
 * Returns array of [itemID, parentIDs] entries
 */
export function getItemEntriesForDept(
  items: { [itemID: string]: ItemParentIDs } | undefined,
  deptID: string,
): Array<[string, ItemParentIDs]> {
  if (!items) return []
  return Object.entries(items).filter(([, p]) => p.deptID === deptID)
}

/**
 * Check if item is selected in V3 bulk selection
 * Handles full priority chain:
 * excludedItems > selectedItems > excludedSubDepts > selectedSubDepts > selectedDepts
 */
export function isItemSelectedV3(
  bulkSelectionsV3: BulkSelectionsMetadata | undefined,
  itemID: string,
  subDeptID: string,
  deptID: string,
): boolean {
  if (!bulkSelectionsV3) return false

  // Check if explicitly excluded (highest priority)
  if (bulkSelectionsV3.excludedItems?.[itemID]) {
    return false
  }

  // Check if explicitly selected
  if (bulkSelectionsV3.selectedItems?.[itemID]) {
    return true
  }

  // Check if parent subdepartment is excluded
  if (bulkSelectionsV3.excludedSubDepartments?.[subDeptID]) {
    return false
  }

  // Check if parent subdepartment is selected
  if (subDeptID && bulkSelectionsV3.selectedSubDepartments?.[subDeptID]) {
    return true
  }

  // Check if parent department is selected
  if (deptID && bulkSelectionsV3.selectedDepartments?.[deptID]) {
    return true
  }

  return false
}

/**
 * Resolve V3 hierarchical selection state into API request params.
 * Consumers should pass these fields directly to updateItemsAvailableStatus.
 */
export function resolveV3SelectionForAPI(
  bulkSelectionsV3: BulkSelectionsMetadata,
) {
  return {
    departmentIDs: Object.keys(bulkSelectionsV3.selectedDepartments ?? {}),
    subDepartmentIDs: Object.keys(
      bulkSelectionsV3.selectedSubDepartments ?? {},
    ),
    itemIDs: Object.keys(bulkSelectionsV3.selectedItems ?? {}),
    excludedSubDepartmentIDs: Object.keys(
      bulkSelectionsV3.excludedSubDepartments ?? {},
    ),
    excludedItemIDs: Object.keys(bulkSelectionsV3.excludedItems ?? {}),
  }
}

export interface V3SelectionSummary {
  /** Total number of affected items across all selection levels */
  itemCount: number
}

/**
 * Compute total item count from V3 hierarchical selections.
 *
 * Department totalItems come directly from the RTK Query cache (stable, loaded once).
 * Subdepartment totalItems are stored in Redux (changes as user navigates).
 *
 * Formula:
 *   + dept.totalItems for each selected dept
 *   - totalItemsPerSubDept[excludedSubDeptID] for subdepts excluded from selected depts
 *   - count(excludedItems belonging to a selected dept, in a non-excluded subdept)
 *   + totalItemsPerSubDept[subDeptID] for explicitly selected subdepts
 *   - count(excludedItems belonging to an explicitly selected subdept)
 *   + count(individually selected items with no selected parent)
 */
export function computeV3SelectionSummary(
  bulkSelectionsV3: BulkSelectionsMetadata,
  departments: Department[],
): V3SelectionSummary {
  const totalItemsPerSubDept = bulkSelectionsV3.totalItemsPerSubDepartment ?? {}
  const excludedItems = bulkSelectionsV3.excludedItems ?? {}
  const excludedSubDepts = bulkSelectionsV3.excludedSubDepartments ?? {}
  const selectedDepts = bulkSelectionsV3.selectedDepartments ?? {}
  const selectedSubDepts = bulkSelectionsV3.selectedSubDepartments ?? {}
  const selectedItems = bulkSelectionsV3.selectedItems ?? {}

  // Build a lookup of deptID -> totalItems from the RTK Query cached departments
  const totalItemsPerDept: { [deptID: string]: number } = {}
  departments.forEach(dept => {
    if (dept.itemClassID) {
      totalItemsPerDept[dept.itemClassID] = dept.totalItems ?? 0
    }
  })

  let itemCount = 0

  // Selected departments: start with full count, subtract excluded subdepts and items
  Object.keys(selectedDepts).forEach(deptID => {
    const deptTotal = totalItemsPerDept[deptID] ?? 0
    const excludedSubDeptItems = Object.entries(excludedSubDepts)
      .filter(([, parentDeptID]) => parentDeptID === deptID)
      .reduce(
        (sum, [subDeptID]) => sum + (totalItemsPerSubDept[subDeptID] ?? 0),
        0,
      )
    const excludedItemsInDept = Object.values(excludedItems).filter(
      p => p.deptID === deptID && !excludedSubDepts[p.subDeptID],
    ).length
    itemCount += Math.max(
      0,
      deptTotal - excludedSubDeptItems - excludedItemsInDept,
    )
  })

  // Explicitly selected subdepts (parent dept not selected)
  Object.keys(selectedSubDepts).forEach(subDeptID => {
    const total = totalItemsPerSubDept[subDeptID] ?? 0
    const excluded = countItemsForSubDept(excludedItems, subDeptID)
    itemCount += Math.max(0, total - excluded)
  })

  // Individually selected items (no selected parent dept or subdept)
  Object.values(selectedItems).forEach(({ subDeptID, deptID }) => {
    if (!selectedDepts[deptID] && !selectedSubDepts[subDeptID]) {
      itemCount += 1
    }
  })

  return { itemCount }
}

export interface CheckboxState {
  checked: boolean
  indeterminate: boolean
}

const UNCHECKED: CheckboxState = { checked: false, indeterminate: false }
const CHECKED: CheckboxState = { checked: true, indeterminate: false }
const INDETERMINATE: CheckboxState = { checked: true, indeterminate: true }

/**
 * Resolve checkbox state when starting from "all selected" and subtracting exclusions.
 * Used when a parent is selected and we need to account for excluded children.
 */
function resolveFromExclusions(
  excludedCount: number,
  total: number,
): CheckboxState {
  if (total > 0 && excludedCount >= total) return UNCHECKED
  if (excludedCount > 0) return INDETERMINATE
  return CHECKED
}

/**
 * Resolve checkbox state when starting from "nothing selected" and adding selections.
 * Used when no parent is selected and we need to account for individually selected children.
 */
function resolveFromSelections(
  selectedCount: number,
  total: number,
): CheckboxState {
  if (selectedCount <= 0) return UNCHECKED
  if (total > 0 && selectedCount >= total) return CHECKED
  return INDETERMINATE
}

/**
 * Compute subdepartment checkbox state based on selection status and item counts
 */
export function computeSubDeptCheckboxState(
  bulkSelectionsV3: BulkSelectionsMetadata | undefined,
  subDeptID: string,
  deptID: string,
  totalItems: number,
): CheckboxState {
  if (!bulkSelectionsV3) return UNCHECKED

  const excludedItems = bulkSelectionsV3.excludedItems || {}
  const selectedItems = bulkSelectionsV3.selectedItems || {}

  // Scenario 1: SubDept is explicitly selected — subtract excluded items
  if (bulkSelectionsV3.selectedSubDepartments?.[subDeptID]) {
    return resolveFromExclusions(
      countItemsForSubDept(excludedItems, subDeptID),
      totalItems,
    )
  }

  // Scenario 2: Parent dept is selected
  if (bulkSelectionsV3.selectedDepartments?.[deptID] === true) {
    // SubDept excluded — only individually re-selected items count
    if (bulkSelectionsV3.excludedSubDepartments?.[subDeptID]) {
      return resolveFromSelections(
        countItemsForSubDept(selectedItems, subDeptID),
        totalItems,
      )
    }
    // SubDept inherits from dept — subtract excluded items
    return resolveFromExclusions(
      countItemsForSubDept(excludedItems, subDeptID),
      totalItems,
    )
  }

  // Scenario 3: No parent selected — only individually selected items count
  return resolveFromSelections(
    countItemsForSubDept(selectedItems, subDeptID),
    totalItems,
  )
}

/**
 * Compute department checkbox state based on selection status and subdept/item counts
 */
export function computeDeptCheckboxState(
  bulkSelectionsV3: BulkSelectionsMetadata | undefined,
  deptID: string,
  totalSubDepts: number,
  totalItemsPerSubDept: { [subDeptID: string]: number },
): CheckboxState {
  if (!bulkSelectionsV3) return UNCHECKED

  // Branch 1: Department is explicitly selected — look for exclusions
  if (bulkSelectionsV3.selectedDepartments?.[deptID] === true) {
    const excludedSubDeptEntries = getSubDeptsForDept(
      bulkSelectionsV3.excludedSubDepartments,
      deptID,
    )
    const excludedItemsForThisDept = getItemsForDept(
      bulkSelectionsV3.excludedItems,
      deptID,
    )
    const selectedItemEntriesForThisDept = getItemEntriesForDept(
      bulkSelectionsV3.selectedItems,
      deptID,
    )

    // Fast path: nothing excluded or re-selected → fully checked
    if (
      excludedSubDeptEntries.length === 0 &&
      excludedItemsForThisDept.length === 0 &&
      selectedItemEntriesForThisDept.length === 0
    ) {
      return CHECKED
    }

    const selectedItemsPerSubDept = groupItemsBySubDept(
      selectedItemEntriesForThisDept,
    )
    const excludedItemsPerSubDept = groupItemsBySubDept(
      bulkSelectionsV3.excludedItems,
      deptID,
    )

    let effectivelyUncheckedCount = 0
    let hasPartial = false

    // Excluded subdepts: check if items were individually re-selected
    excludedSubDeptEntries.forEach(([subDeptID]) => {
      const total = totalItemsPerSubDept[subDeptID] ?? 0
      const reselected = selectedItemsPerSubDept[subDeptID] ?? 0
      if (total > 0 && reselected >= total) {
        // Fully re-selected → not effectively unchecked
      } else if (reselected > 0) {
        hasPartial = true
      } else {
        effectivelyUncheckedCount += 1
      }
    })

    // Non-excluded subdepts with all items excluded at item level
    Object.entries(excludedItemsPerSubDept).forEach(([subDeptID, count]) => {
      if (bulkSelectionsV3.excludedSubDepartments?.[subDeptID]) return // handled above
      const total = totalItemsPerSubDept[subDeptID] ?? 0
      if (total > 0 && count >= total) {
        effectivelyUncheckedCount += 1
      }
    })

    if (totalSubDepts > 0 && effectivelyUncheckedCount >= totalSubDepts)
      return UNCHECKED

    const hasAnyExclusions =
      effectivelyUncheckedCount > 0 ||
      excludedItemsForThisDept.length > 0 ||
      hasPartial

    return hasAnyExclusions ? INDETERMINATE : CHECKED
  }

  // Branch 2: Department NOT selected — look for child selections
  const selectedSubDeptsForThisDept = getSubDeptsForDept(
    bulkSelectionsV3.selectedSubDepartments,
    deptID,
  )
  const selectedItemsForThisDept = getItemsForDept(
    bulkSelectionsV3.selectedItems,
    deptID,
  )

  if (
    selectedSubDeptsForThisDept.length === 0 &&
    selectedItemsForThisDept.length === 0
  ) {
    return UNCHECKED
  }

  const excludedItems = bulkSelectionsV3.excludedItems || {}
  const selectedItemsPerSubDept = groupItemsBySubDept(
    bulkSelectionsV3.selectedItems,
    deptID,
  )
  const countedSubDeptIDs = new Set<string>()
  let effectiveSelectedCount = 0
  let hasPartialSubDept = false

  // Explicitly selected subdepts (count unless all items are excluded)
  selectedSubDeptsForThisDept.forEach(([subDeptID]) => {
    const total = totalItemsPerSubDept[subDeptID] ?? 0
    const excludedCount = countItemsForSubDept(excludedItems, subDeptID)
    if (total === 0 || excludedCount < total) {
      effectiveSelectedCount += 1
      countedSubDeptIDs.add(subDeptID)
      if (excludedCount > 0) hasPartialSubDept = true
    }
  })

  // Subdepts where all items were individually selected
  Object.entries(selectedItemsPerSubDept).forEach(([subDeptID, count]) => {
    if (countedSubDeptIDs.has(subDeptID)) return
    const total = totalItemsPerSubDept[subDeptID] ?? 0
    if (total > 0 && count >= total) {
      effectiveSelectedCount += 1
      countedSubDeptIDs.add(subDeptID)
    } else if (count > 0) {
      hasPartialSubDept = true
    }
  })

  if (totalSubDepts > 0 && effectiveSelectedCount >= totalSubDepts) {
    return hasPartialSubDept ? INDETERMINATE : CHECKED
  }

  if (effectiveSelectedCount === 0 && !hasPartialSubDept) return UNCHECKED

  return INDETERMINATE
}
